import { Suspense, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { lazyWithRetry } from "src/utils/lazyWithRetry.js";
import {
  GOOGLE_CONNECT_STATES,
  turnOffGoogleConnectState,
} from "src/utils/googleConnect.js";
import {
  clearChannelDecision,
  clearGoogleConnectSurfaces,
  usePendingChannelDecision,
} from "src/utils/googleConnectIntercept.js";
import {
  RECONNECT_STATE,
  startGoogleConnectOauth,
} from "src/utils/googleConnectOauth.js";

// Deferred so the dialog stays out of the entry bundle: only broken and inactive
// users can ever open it, and this host renders for everyone.
const GoogleConnectInterceptDialog = lazyWithRetry(() =>
  import("src/components/blocks/dialogs/GoogleConnectInterceptDialog.js")
);
/**
 * The single home for the add-moment intercept. Every surface that offers it asks
 * this host through the store, so only one can ever be open.
 */
export default function GoogleConnectInterceptHost() {
  // Mounted at app root, so tolerate rendering outside AuthProvider.
  const { user } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  const location = useLocation();
  const request = usePendingChannelDecision();

  // The intercept holds a callback into the page that opened it, so it does not
  // survive a navigation.
  useEffect(() => {
    clearGoogleConnectSurfaces();
  }, [location.pathname]);

  // Sign-out ends the session these dialogs belonged to.
  useEffect(() => {
    if (user) return;
    clearGoogleConnectSurfaces();
  }, [user]);

  const track = (name) => {
    customEvent({
      name,
      category: "GoogleConnect",
      label: "Google Connect add intercept",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_id: request?.calendar?._id,
        calendar_handle: request?.calendar?.handle,
        calendar_name: request?.calendar?.name,
        connection_state: request?.state,
      },
    });
  };

  // The impression is the funnel's denominator: without it the click events read
  // as conversions with no traffic.
  useEffect(() => {
    if (!request) return;
    track("google_connect_add_intercept_shown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const handleReconnect = () => {
    track("google_connect_add_intercept_reconnect_clicked");
    clearChannelDecision();
    startGoogleConnectOauth(RECONNECT_STATE);
  };

  const handleResubscribe = () => {
    track("google_connect_add_intercept_resubscribe_clicked");
    clearChannelDecision();
    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
      state: { source: "google_connect_add_intercept" },
    });
  };

  const handleOffboard = () => {
    track("google_connect_add_intercept_offboard_clicked");
    clearChannelDecision();
    navigate(getPageRoute("help", "GOOGLE_CONNECT_OFF"), {
      state: turnOffGoogleConnectState(location),
    });
  };

  const handleAddByLink = () => {
    track("google_connect_add_intercept_link_fallback_chosen");
    const addByLink = request?.onAddByLink;
    clearChannelDecision();
    addByLink?.();
  };

  const handleDismiss = () => {
    track("google_connect_add_intercept_dismissed");
    clearChannelDecision();
  };

  return (
    <>
      {request && (
        <Suspense fallback={null}>
          <GoogleConnectInterceptDialog
            open
            state={request.state ?? GOOGLE_CONNECT_STATES.BROKEN}
            cancelledOnPurpose={Boolean(user?.hasEndedVoluntaryCancellation)}
            onClose={handleDismiss}
            onReconnect={handleReconnect}
            onResubscribe={handleResubscribe}
            onOffboard={handleOffboard}
            onAddByLink={handleAddByLink}
          />
        </Suspense>
      )}
    </>
  );
}
